import type { TFunction } from 'i18next';
import type { InvoiceBreakdownKey, InvoiceStatus, IStatementLine, LedgerInvoiceStatus } from './types';

// Display helpers for /billing — money arrives in integer minor units (exponent 2 for
// every currency Nexa bills today), rates in basis points.

export function formatMinor(minor: number, currency: string, language: string): string {
  return new Intl.NumberFormat(language, { style: 'currency', currency }).format(minor / 100);
}

export function formatRateBp(bp: number, language: string): string {
  return new Intl.NumberFormat(language, { style: 'percent', maximumFractionDigits: 2 }).format(bp / 10_000);
}

/** "Noviembre de 2026" — the invoiced month, named in the reader's language, first letter up. */
export function formatPeriod(periodStart: string, language: string): string {
  const month = new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${periodStart}T00:00:00Z`),
  );
  return month.charAt(0).toLocaleUpperCase(language) + month.slice(1);
}

export function formatDay(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(iso));
}

/** Days in the invoiced month — a monthly-fee line with fewer days was prorated. */
export function daysInPeriod(periodStart: string, periodEnd: string): number {
  return Math.round((Date.parse(`${periodEnd}T00:00:00Z`) - Date.parse(`${periodStart}T00:00:00Z`)) / 86_400_000);
}

/** "28/10 22:14" — a ledger instant, always in UTC (the statement's column says so). */
export function formatStamp(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

/** `2026-10` → "October 2026", in the reader's language. */
export function formatPeriodKey(periodKey: string, language: string): string {
  return formatPeriod(`${periodKey}-01`, language);
}

/** The record locator out of a PNR URN (`urn:pnr:K7X2QD:vendor:x` → `K7X2QD`). */
export function locatorOf(pnrUrn: string | null): string {
  return pnrUrn ? pnrUrn.replace(/^urn:pnr:/, '').split(':')[0] || pnrUrn : '—';
}

/** The BFF answered 404 — the disruption has no billed line, the invoice is a draft, … */
export function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

/** The line's service: `DISRUPTION_FEE` for the per-PNR fee, else its service type. */
export function lineKey(line: Pick<IStatementLine, 'kind' | 'serviceType'>): InvoiceBreakdownKey {
  return line.kind === 'DISRUPTION_FEE' || !line.serviceType ? 'DISRUPTION_FEE' : line.serviceType;
}

/** The missing-evidence codes in words (`billing:missing.*`); an unknown code reads as itself. */
export function missingText(codes: readonly string[], t: TFunction): string {
  return codes.map((c) => t(`billing:missing.${c}`, { defaultValue: c })).join(', ');
}

/**
 * Finance's invoice status in the words of the /billing list (`admin:billing.status.*`),
 * by the same rule as operator-bff's `statusOf` (invoices@v2): ISSUED past its due date
 * reads overdue. Without a due date an ISSUED invoice reads open.
 */
export function invoiceStatusOf(status: LedgerInvoiceStatus, dueAt: string | null, now = Date.now()): InvoiceStatus {
  if (status === 'PAID') return 'paid';
  if (status === 'VOID') return 'void';
  return dueAt && now > Date.parse(dueAt) ? 'overdue' : 'open';
}
